/*
 * Copyright (C) 2022 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 */
import { __awaiter } from "tslib";
import { AppSyncClientFactory } from "../backend/AppSyncClientFactory";
import { Service } from "../backend/AppSyncClientProvider";
import { AWSDataSet } from "../data/AWSDataSet";
import { AWSLatestData } from "../data/AWSLatestData";
import { AWSSessionSet } from "../data/AWSSessionSet";
import { AWSEventSet } from "../events/AWSEventSet";
import { Device, } from "./Device";
import { ShadowSubscriptionManager } from "./ShadowSubscriptionManager";
import { narrowDownAttributeTypes } from "../backend/AWSBackend";
import { DevicesDeleteDocument, DevicesEncryptDocument, DevicesRemoteLoggingSetDocument, DevicesStatesGetDocument, DevicesUpdateDocument, } from "../../generated/gqlDevice";
import { PromiseSemaphore } from "../utils/PromiseSemaphore";
import { AWSThingGroup } from "./AWSThingGroup";
import { throwGQLError } from "../utils";
/**
 * Base-class for typed AWS Thing implementations. Do not create this directly.
 *
 * This class is no longer abstract since TypeScript does not allow for run-time type comparison against an
 * abstract class (since abstract things do not exist in TypeScript at run-time).
 */
export class AWSThing extends Device {
    /*
     * DO NOT CALL DIRECTLY
     *
     * This constructor needs to be public so {@link EntityRelationCache} can use it for type checks.
     */
    constructor(type, backend, params) {
        super();
        this.type = type;
        this.backend = backend;
        this.entityType = AWSThing;
        this.groupSemaphore = new PromiseSemaphore(() => this.backend.linkDeviceGroupsForDevice(this));
        this.deviceId = params.deviceId;
        this.attributes = params.attributes;
        this.controlLevel = params.controlLevel;
        this.groupIds = params.groupIds;
    }
    static instanceOf(value) {
        return value instanceof AWSThing;
    }
    getGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.groupSemaphore.guard();
            return this.backend.entityRelationCache.listFor(this, AWSThingGroup);
        });
    }
    getAttribute(key) {
        var _a, _b;
        return (_b = (_a = this.attributes) === null || _a === void 0 ? void 0 : _a.find((attribute) => attribute.key === key)) === null || _b === void 0 ? void 0 : _b.value;
    }
    getAttributes() {
        var _a;
        return (_a = this.attributes) !== null && _a !== void 0 ? _a : [];
    }
    getId() {
        return this.deviceId;
    }
    getType() {
        return this.type;
    }
    getControlLevel() {
        return this.controlLevel;
    }
    getState() {
        return this.state;
    }
    getGroupIds() {
        return this.groupIds;
    }
    // TODO: Consider moving this functionality inside DeviceState
    addObserver(observer) {
        super.addObserver(observer);
        ShadowSubscriptionManager.instance.addListener(this);
    }
    removeObserver(observer) {
        super.removeObserver(observer);
        ShadowSubscriptionManager.instance.removeListener(this);
    }
    onRelationChange(change) {
        if (change.ofType(AWSThingGroup)) {
            this.notifyAction((observer) => { var _a; return (_a = observer.onDeviceGroupsChanged) === null || _a === void 0 ? void 0 : _a.call(observer, this); });
        }
    }
    init(existingState) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (existingState) {
                    const { desired, reported, timestamp, version, connectionState } = existingState;
                    this.state = this.createState(timestamp !== null && timestamp !== void 0 ? timestamp : undefined, version !== null && version !== void 0 ? version : undefined, reported ? JSON.parse(reported) : undefined, desired ? JSON.parse(desired) : undefined, connectionState);
                    return;
                }
                const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
                const deviceStateResponse = yield client.query(DevicesStatesGetDocument, {
                    deviceId: this.deviceId,
                }, 
                // mobile-app specific: Force fetch or will return old data from cache
                {
                    fetchPolicy: "network-only",
                });
                const { desired, reported, timestamp, version, connectionState } = (_a = deviceStateResponse.data.devicesStatesGet) !== null && _a !== void 0 ? _a : {};
                this.state = this.createState(timestamp !== null && timestamp !== void 0 ? timestamp : undefined, version !== null && version !== void 0 ? version : undefined, reported ? JSON.parse(reported) : undefined, desired ? JSON.parse(desired) : undefined, connectionState
                    ? { connected: connectionState.connected, updatedTimestamp: connectionState.updatedTimestamp }
                    : undefined);
            }
            catch (error) {
                console.error("init", error);
            }
        });
    }
    setState(timestamp, version, current, next, connectionState) {
        var _a, _b;
        if (version && ((_a = this.state) === null || _a === void 0 ? void 0 : _a.version) && version < this.state.version) {
            console.log("New state version " + version + " is older than " + ((_b = this.state) === null || _b === void 0 ? void 0 : _b.version));
            return;
        }
        // TODO: Consider implementing a method that just updates the existing state
        this.state = this.createState(timestamp, version, current, next, connectionState);
        this.notifyAction((observer) => { var _a; return (_a = observer.onDeviceStateUpdated) === null || _a === void 0 ? void 0 : _a.call(observer, this); });
    }
    getSessionSet(startTimestamp, endTimestamp) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const sessionSet = new AWSSessionSet(this.deviceId, startTimestamp, endTimestamp);
                yield sessionSet.fetch();
                return sessionSet;
            }
            catch (error) {
                console.error("getSessionSet", error);
            }
        });
    }
    getDataSet(startTimestamp, endTimestamp) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.dataSet &&
                    this.dataSet.getTimePeriod().startTimestamp === startTimestamp &&
                    this.dataSet.getTimePeriod().endTimestamp === endTimestamp) {
                    return this.dataSet;
                }
                const dataSet = new AWSDataSet(this.deviceId, this.type, startTimestamp, endTimestamp);
                yield dataSet.fetch();
                this.dataSet = dataSet;
                return this.dataSet;
            }
            catch (error) {
                console.error("getDataSet", error);
            }
        });
    }
    getLatestData() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this.latestData) {
                    this.latestData = new AWSLatestData(this.deviceId, this.type);
                    yield this.latestData.fetch();
                }
                return this.latestData;
            }
            catch (error) {
                console.error("getLatestData", error);
            }
        });
    }
    getEventSet(startTimestamp, endTimestamp, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.eventSet &&
                    !(options === null || options === void 0 ? void 0 : options.force) &&
                    startTimestamp === this.eventSet.getTimePeriod().startTimestamp &&
                    endTimestamp === this.eventSet.getTimePeriod().endTimestamp) {
                    return this.eventSet;
                }
                if (this.eventSet) {
                    this.eventSet.changeTimePeriod(startTimestamp, endTimestamp);
                }
                else {
                    // fetch new event set
                    const eventSet = new AWSEventSet(this.deviceId, startTimestamp, endTimestamp);
                    this.eventSet = eventSet;
                }
                yield this.eventSet.fetch();
                return this.eventSet;
            }
            catch (error) {
                console.error("getEventSet", error);
            }
        });
    }
    // TODO: can user set null values for attr's value?
    updateAttributes(attributes) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
                const response = yield client.mutate(DevicesUpdateDocument, {
                    deviceId: this.deviceId,
                    attributes,
                });
                this.attributes = narrowDownAttributeTypes((_c = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.devicesUpdate) === null || _b === void 0 ? void 0 : _b.attr) !== null && _c !== void 0 ? _c : []);
            }
            catch (error) {
                console.error("updateAttributes", error);
            }
        });
    }
    delete() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
                yield client.mutate(DevicesDeleteDocument, { deviceId: this.deviceId });
                this.backend.removeLocal(this);
                return true;
            }
            catch (error) {
                console.error("Delete device failed: ", error);
            }
            return false;
        });
    }
    setRemoteLogging(cloudLoggingState) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
                const response = yield client.mutate(DevicesRemoteLoggingSetDocument, {
                    deviceId: this.deviceId,
                    cloudLoggingState: cloudLoggingState,
                });
                return (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.devicesRemoteLoggingSet) !== null && _b !== void 0 ? _b : false;
            }
            catch (error) {
                console.error(`setRemoteLogging for device ${this.deviceId} failed: `, error);
            }
            return false;
        });
    }
    /**
     * Encrypt a message.
     *
     * Encryption API uses public key (RSA) encryption where the public key is derived from device certificate.
     * @param message Message to encrypt
     * @returns Encrypted message
     */
    encrypt(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const client = AppSyncClientFactory.createProvider().getTypedClient(Service.DEVICE);
            const response = yield client.query(DevicesEncryptDocument, { deviceId: this.deviceId, message });
            if (!response.data.devicesEncrypt) {
                throwGQLError(response, "Encryption failed");
            }
            return response.data.devicesEncrypt;
        });
    }
    // OVERRIDE THIS
    createState(_timestamp, _version, _reported, _desired, _connectionState) {
        throw new Error("AWSThing MUST NOT be created directly");
    }
    // OVERRIDE THIS
    getIcon() {
        throw new Error("AWSThing MUST NOT be created directly");
    }
}
